import { z } from 'zod'
import { asEnumType } from '@gqloom/zod'

export const FeatureKeys = {
  // ---------- BASIC tier ----------
  // Location Management
  Regions: 'regions',
  LocationSettings: 'location_settings',
  RoomManagement: 'room_management',
  CapacityManagement: 'capacity_management',
  BrandCustomisation: 'brand_customisation',
  WebsiteEmbedWidgets: 'website_embed_widgets',
  // Memberships & Packages
  MembershipsPackages: 'memberships_packages',
  ServiceLifecycle: 'service_lifecycle',
  CreditManagement: 'credit_management',
  ServiceAddOns: 'service_add_ons',
  PlanChange: 'plan_change',
  // Scheduling & Class Management
  SchedulingClasses: 'scheduling_classes',
  InstructorAssignment: 'instructor_assignment',
  // Bookings & Attendance
  Bookings: 'bookings',
  BookingRules: 'booking_rules',
  Waitlist: 'waitlist',
  Checkin: 'checkin',
  BarcodeCheckin: 'barcode_checkin',
  AttendanceTracking: 'attendance_tracking',
  SessionFeedback: 'session_feedback',
  // Client Management
  ClientManagement: 'client_management',
  ClientCustomProperties: 'client_custom_properties',
  ClientFiles: 'client_files',
  ClientAgreements: 'client_agreements',
  CustomForms: 'custom_forms',
  ClientImport: 'client_import',
  SupportChat: 'support_chat',
  // Billing & Payments
  Invoicing: 'invoicing',
  BillingPayments: 'billing_payments',
  PaymentNotifications: 'payment_notifications',
  FailedPaymentRecovery: 'failed_payment_recovery',
  PaymentMethods: 'payment_methods',
  Refunds: 'refunds',
  AccountCredits: 'account_credits',
  Disputes: 'disputes',
  SitePayouts: 'site_payouts',
  PaymentGateways: 'payment_gateways',
  TaxConfiguration: 'tax_configuration',
  Proration: 'proration',
  // Retail & Point of Sale
  RetailPos: 'retail_pos',
  TerminalReaders: 'terminal_readers',
  PromoCodes: 'promo_codes',
  // Reporting (Basic)
  StandardReports: 'standard_reports',
  // Staff Management
  StaffManagement: 'staff_management',
  AccessPolicies: 'access_policies',
  StaffSmartGroups: 'staff_smart_groups',
  // Marketing & Communications (fka Grow Lite)
  CommunicationTemplates: 'communication_templates',
  SmsMessaging: 'sms_messaging',
  PushNotifications: 'push_notifications',
  LeadForms: 'lead_forms',
  SmartContactLists: 'smart_contact_lists',
  SocialMediaSettings: 'social_media_settings',
  // Content & Engagement
  ContentFaqs: 'content_faqs',
  // Member App
  MemberApp: 'member_app',
  MobileBooking: 'mobile_booking',
  MobileCheckin: 'mobile_checkin',
  // Integrations (Basic) & APIs
  ClasspassIntegration: 'classpass_integration',
  ZapierIntegration: 'zapier_integration',
  ZoomIntegration: 'zoom_integration',
  WellhubIntegration: 'wellhub_integration',
  InceptionIntegration: 'inception_integration',
  TopazIntegration: 'topaz_integration',
  // AI & Automation
  HapanaAssistant: 'hapana_assistant',
  // Infrastructure, Security & Compliance
  SharedInfrastructure: 'shared_infrastructure',
  Rbac: 'rbac',
  // Professional Services
  ProfessionalServices: 'professional_services',

  // ---------- PREMIUM tier ----------
  // Location / Brand Management
  MultiLocation: 'multi_location',
  MultiRoom: 'multi_room',
  SpotBasedBooking: 'spot_based_booking',
  // Membership & Packages
  SharedCredits: 'shared_credits',
  GuestPasses: 'guest_passes',
  ReferAFriend: 'refer_a_friend',
  // Client Management
  BookABuddy: 'book_a_buddy',
  SmartClientGroups: 'smart_client_groups',
  // Bookings & Attendance
  AdvanceBooking: 'advance_booking',
  // Retail & Point of Sale
  GiftCards: 'gift_cards',
  // Reporting & Analytics
  BiDashboards: 'bi_dashboards',
  // Staff Management
  Payroll: 'payroll',
  Commissions: 'commissions',
  // Marketing & Communications (fka Grow Essentials)
  CampaignTemplates: 'campaign_templates',
  Whatsapp: 'whatsapp',
  // Content & Engagement
  ContentSuite: 'content_suite',
  // Member App
  ContentDelivery: 'content_delivery',
  // Integrations & API Access (Read-Only)
  ReadApi: 'read_api',
  ApiWebhooksRead: 'api_webhooks_read',
  ApiSandbox: 'api_sandbox',
  ApiPremiumActions: 'api_premium_actions',
  FacebookPixel: 'facebook_pixel',
  GoogleTagManager: 'google_tag_manager',
  GrowHqManagement: 'grow_hq_management',
  AccurofitIntegration: 'accurofit_integration',
  SpiviIntegration: 'spivi_integration',
  // Marketplace is the parent surface (the Control Center marketplace + its provider list); the
  // individual providers inside it are their own features, so a brand can have the Marketplace
  // without a specific provider integration (HubSpot, eGym).
  Marketplace: 'marketplace',
  HubspotIntegration: 'hubspot_integration',
  EgymIntegration: 'egym_integration',

  // ---------- PRO tier ----------
  // Location / Brand Management
  HqAccount: 'hq_account',
  HeadOfficeContent: 'head_office_content',
  SiteGroups: 'site_groups',
  AdminAccessGroups: 'admin_access_groups',
  StaffIpRestrictions: 'staff_ip_restrictions',
  AdminProvisioning: 'admin_provisioning',
  CrossLocationAnalytics: 'cross_location_analytics',
  ConnectedSharedSites: 'connected_shared_sites',
  // Membership & Packages
  TieredPerks: 'tiered_perks',
  HolidayFee: 'holiday_fee',
  NoShowFee: 'no_show_fee',
  // Client Management
  MemberMovement: 'member_movement',
  FamilyAccounts: 'family_accounts',
  // Scheduling & Class Management
  ClassCovers: 'class_covers',
  // Billing & Payments
  CorporatePayouts: 'corporate_payouts',
  RoyaltyDistribution: 'royalty_distribution',
  // (Custom) Reporting & Analytics
  ScheduledReports: 'scheduled_reports',
  BigqueryReports: 'bigquery_reports',
  KpiPerformanceReport: 'kpi_performance_report',
  // Marketing & Communications
  BrandWideTemplates: 'brand_wide_templates',
  // Member App
  CustomBrandedApp: 'custom_branded_app',
  MemberAppPlanYourVisit: 'member_app_plan_your_visit',
  // Integrations API Access (Read/Write)
  FullApiReadWrite: 'full_api_read_write',
  WebhookEventsReadWrite: 'webhook_events_read_write',
  ApiSelfServiceKeys: 'api_self_service_keys',
  BrandApiKeys: 'brand_api_keys',
  LocationApiKeys: 'location_api_keys',
  // Infrastructure, Security & Compliance
  IsolatedDatabase: 'isolated_database',
  FullAuditLogs: 'full_audit_logs',

  // ---------- ACCESS theme ----------
  AccessMacDoor: 'access_mac_door',
  AccessScheduling: 'access_scheduling',
  AccessEventLogging: 'access_event_logging',
  AccessReporting: 'access_reporting',
  AccessSiteDoorManagement: 'access_site_door_management',
  AccessDigitalExperience: 'access_digital_experience',
  AccessController: 'access_controller',

  // ---------- EXPERIENCE theme ----------
  ExperiencePerformance: 'experience_performance',
  ExperienceChallenges: 'experience_challenges',
  ExperienceLeaderboards: 'experience_leaderboards',
  ExperienceStreaks: 'experience_streaks',
  ExperienceBadges: 'experience_badges',
  ExperienceMilestones: 'experience_milestones',

  // ---------- RETENTION theme ----------
  RetentionDashboards: 'retention_dashboards',
  RetentionAtRisk: 'retention_at_risk',
  RetentionChurnAnalysis: 'retention_churn_analysis',
  RetentionEngagement: 'retention_engagement',
  RetentionWinback: 'retention_winback',
  RetentionAttendanceTrends: 'retention_attendance_trends',

  // ---------- HAPANA AI theme ----------
  AiActions: 'ai_actions',
  AiDataInsights: 'ai_data_insights',
  AiPredictive: 'ai_predictive',
  AiRecommendations: 'ai_recommendations',
  AiCharts: 'ai_charts',
  AiMetering: 'ai_metering',
  AiNaturalLanguageAnalytics: 'ai_natural_language_analytics',
} as const

// Zod v4 accepts a const object directly, which preserves the literal-union type.
// Do NOT use `z.enum(Object.values(FeatureKeys) as [string, ...string[]])` — that
// widens FeatureKey to plain `string`. Matches shared/schemas/payments/invoice-schemas.ts.
export const featureKeySchema = z.enum(FeatureKeys).register(asEnumType, {
  name: 'FeatureKey',
})

export type FeatureKey = z.infer<typeof featureKeySchema>
